import React, { useCallback, useEffect, useRef, useState } from 'react'
import { TitleBar } from '../../common'
import { TITLES, ROUTES, LOCAL_STORAGE } from '../../../constants'
import { manufacturingLogsData } from '../../../service/logs'
import { PaginatedTable } from '../../common'
import { LOGS_COLUMN } from '../../../constants'
import { logsDataCompiler } from '../../../utils/dataCompiler/logsDataCompiler'
import { SearchBar } from '../../common'
import { useSearchParams } from "../../../backend/navigation";
import { Button } from '../../shared'
import { BUTTON } from '../../../constants'
import { DropDown } from '../../common'
import { getLocalStorageItem } from '../../../utils'
import DateTimeRangePicker from '../../common/DateTimeRangePicker'

export const LogsContent = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [logsData, setLogsData] = useState([]);
    const [readError, setReadError] = useState(false);
    const [pageNumber, setPageNumber] = useState(parseInt(searchParams.get('page')) || 1);
    const [paginationData, setPaginationData] = useState({});
    const [filteredColumn] = useState(() => {
        const vendorType = getLocalStorageItem(LOCAL_STORAGE.VENDOR_TYPE);
        return LOGS_COLUMN.filter((col) => vendorType === 'fabric'
            ? col.name !== 'Total Qty Produced'
            : vendorType !== 'dyeing' || !['Total Body Produced', 'Total Rib Produced'].includes(col.name));
    });
    const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
    const [search, setSearch] = useState(false);
    const [sort1, setSort1] = useState(searchParams.get('filter') || LOGS_COLUMN[8].name);
    const [sortingArray1, setSortingArray1] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [showDateRange, setShowDateRange] = useState(() => Boolean(searchParams.get('start_date') || searchParams.get('end_date')));
    const [startDate, setStartDate] = useState(searchParams.get('start_date') || '');
    const [endDate, setEndDate] = useState(searchParams.get('end_date') || '');

    const [query, setQuery] = useState(() => ({ term: searchTerm, filter: sort1, page: pageNumber, start: startDate, end: endDate }));
    const requestedQuery = useRef(null);
    const requestGeneration = useRef(0);
    useEffect(() => () => {
      requestGeneration.current += 1;
      requestedQuery.current = null;
  }, []);

    const fetchLogsData = useCallback(async ({ term, filter, page, start, end }) => {
        const generation = ++requestGeneration.current;
        const params = {
            page_number: page,
            search: term ? { invoice_number: term, 'log_lines.lot': term, id: term } : {},
            sort: { selection: filter !== LOGS_COLUMN[8].name ? { status: filter.toLowerCase() } : {} },
            start_date: start || undefined,
            end_date: end || undefined,
        };
        const url = { page: page.toString() };
        if (term) url.search = term;
        if (filter !== LOGS_COLUMN[8].name) url.filter = filter;
        if (start) url.start_date = start;
        if (end) url.end_date = end;
        setSearchParams(url, { replace: true });
        try {
            setIsLoading(true);
            setReadError(false);
            const response = await manufacturingLogsData({ data: params });
            if (generation !== requestGeneration.current) return;
            if (response?.data?.records?.length > 0) {
                setLogsData(response.data.records);
                setPaginationData(response.data.params);
                const name = LOGS_COLUMN[8].dataField[0];
                if (Array.isArray(response.data[name])) setSortingArray1(response.data[name]);
            } else {
                setLogsData([]);
                setPaginationData({});
                setReadError(!response?.data);
            }
        } catch {
            if (generation !== requestGeneration.current) return;
            setLogsData([]);
            setPaginationData({});
            setReadError(true);
        } finally {
            if (generation === requestGeneration.current) setIsLoading(false);
        }
    }, [setSearchParams]);

    useEffect(() => {
        // Navigation changes its setter identity; a submitted query executes once.
        if (requestedQuery.current === query) return;
        requestedQuery.current = query;
        fetchLogsData(query);
    }, [fetchLogsData, query]);

    const submitQuery = ({ term = searchTerm.trim(), filter = sort1, page = 1, start = startDate, end = endDate } = {}) => {
        setPageNumber(page);
        setQuery({ term, filter, page, start, end });
    };
    const handleSearch = (value) => {
        setSearch(value);
        if (value) submitQuery();
    };
    const handlePageChange = (value) => {
        const page = typeof value === 'function' ? value(pageNumber) : value;
        if (page && page !== pageNumber) submitQuery({ page });
    };
    const handleSortChange = (filter) => {
        setSort1(filter);
        submitQuery({ filter });
    };
    const clearSearch = () => {
        setSearch(false);
        submitQuery({ term: '' });
    };
    const resetFilter = () => {
        setSort1(LOGS_COLUMN[8].name);
        setStartDate('');
        setEndDate('');
        setShowDateRange(false);
        submitQuery({ filter: LOGS_COLUMN[8].name, start: '', end: '' });
    };
    const handleApply = ({ start, end }) => {
        if (!start || !end) return;
        setStartDate(start);
        setEndDate(end);
        submitQuery({ start, end });
    };

    return (
        <>
            {readError && <div role="alert">
                <p>Unable to load process logs.</p>
                <button type="button" onClick={() => setQuery({ ...query })}>Retry</button>
            </div>}
            <TitleBar title={[{ text: TITLES.LOGS, route: ROUTES.LOGS }]} />
            {/* 🔹 Search + Filter + Date Range Row */}
            <div className="d-flex flex-wrap align-items-center gap-3 w-100 mb-3">

                {/* Left side: Search and Filter side-by-side */}
                {/* <div className="d-flex align-items-center flex-wrap gap-3 flex-grow-1"> */}
                <SearchBar
                    search={search}
                    setSearchTerm={setSearchTerm}
                    searchTerm={searchTerm}
                    setSearch={handleSearch}
                    onDefaultSearch={clearSearch}
                />

                <Button
                    clickHandler={() => setShowDateRange(!showDateRange)}
                    className="blue-btn d-flex align-items-center justify-content-center px-3 mb-3"
                >
                    Apply Date Range
                </Button>

                <div className="d-flex align-items-center gap-2 mb-3">
                    <DropDown dropdownMenu={sortingArray1} value={sort1} setValue={handleSortChange} />
                    {(sort1 !== LOGS_COLUMN[8].name || startDate || endDate) && (
                        <Button
                            clickHandler={resetFilter}
                            className="blue-btn d-flex align-items-center justify-content-center px-3"
                        >
                            {BUTTON.CLEAR_FILTERS}
                        </Button>
                    )}
                </div>
            </div>

            {/* 🔹 Animated Collapsible Date Range Picker */}
            <div
                className={`collapse ${showDateRange ? 'show' : ''} mb-3`}
                style={{ transition: 'height 0.9s ease' }}
            >
                <div className="p-3 border rounded bg-white shadow-sm">
                    <DateTimeRangePicker onApply={handleApply} startDateTime={startDate} endDateTime={endDate} />
                </div>
            </div>
            {!readError && (Array.isArray(logsData) ? (
                <PaginatedTable
                    columns={filteredColumn}
                    initialData={logsData}
                    colFilter={true}
                    to={ROUTES.LOG_DETAILS}
                    compiler={{ use: true, tool: logsDataCompiler }}
                    className="table-4"
                    pageNumber={pageNumber}
                    setPageNumber={handlePageChange}
                    paginationData={paginationData}
                    isLoading={isLoading}
                />
            ) : (
                <div>
                    <h4 className="text-center px-1 py-5">No Records Found</h4>
                </div>
            ))}
        </>
    )
}